import { useApp } from "../app/AppContext.jsx";
import { api } from "../lib/api.js";
import { useFetch } from "../lib/useFetch.js";
import { money } from "../lib/format.js";

/**
 * What comparable cars actually hammered for in Japan.
 *
 * The one number on this page that is not ours. Everything else in the quote is our own
 * arithmetic over our own rulesets; this is the market answering back, and it is the only
 * way a buyer can tell whether the landed price above is a good one.
 *
 * Three deliberate choices:
 *
 *  1. The sample size is as prominent as the number. A median over four sales and a median
 *     over forty are different objects and the page has to say which it is looking at.
 *  2. Lots that did not meet reserve are shown, not hidden. "Nine of twenty went unsold"
 *     says more about a model's market than any average does.
 *  3. It is attributed. We did not stand in the hall — this is a third-party observation of
 *     the wholesale market, and it is labelled as one rather than dressed up as our data.
 *
 * Renders nothing at all when there is no usable sample. A market note with an n of one is
 * worse than silence.
 */
const MIN_SAMPLE = 3;

export default function MarketNote({ make, model, year }) {
  const { t, lang } = useApp();
  const { data } = useFetch(
    () => (make ? api.market({ make, model, year }) : Promise.resolve(null)),
    `market:${make}:${model}:${year}`,
  );

  if (!data || data.sold < MIN_SAMPLE || data.median == null) return null;

  // Name the sample honestly. The source carries twenty results per model across fifteen
  // model years, so a year match usually finds two sales and the server widens; saying
  // "a comparable 2020" over a sample spanning 2009–2025 would be the lie of omission.
  const name = [make, model].filter(Boolean).join(" ");
  const what = data.scope === "make" ? make
    : data.scope === "model" ? t("market.scope.model", { what: name })
    : data.yearFrom && data.yearTo && data.yearFrom !== data.yearTo
      ? t("market.scope.years", { what: name, from: data.yearFrom, to: data.yearTo })
      : name;

  const band = data.p25 && data.p75 && data.p75 > data.p25
    ? `${money(data.p25, "EUR", "FR")} – ${money(data.p75, "EUR", "FR")}`
    : null;

  return (
    <div className="notice" style={{ marginTop: 20 }}>
      <p className="label" style={{ display: "block", marginBottom: 10 }}>{t("market.title")}</p>

      <p style={{ color: "var(--text)", fontSize: 15 }}>
        {t("market.headline", {
          amount: money(data.median, "EUR", "FR"),
          n: data.sold,
          what,
        })}
      </p>

      {band && (
        <p className="small muted" style={{ marginTop: 8 }}>
          {t("market.spread", { band })}
        </p>
      )}

      {data.unsold > 0 && (
        <p className="small muted" style={{ marginTop: 6 }}>
          {/* "1 lots sur 7 n'ont pas atteint" — a single unsold lot is the common case, and
              three of the four languages inflect for it. A `.one` sibling key is enough; a
              general plural engine for one string would be more machinery than message. */}
          {t(data.unsold === 1 ? "market.unsold.one" : "market.unsold", { n: data.unsold, total: data.n })}
        </p>
      )}

      <p className="tiny muted" style={{ marginTop: 12, lineHeight: 1.6 }}>
        {t("market.basis")}
        {data.from && data.to ? ` ${t("market.period", { from: data.from, to: data.to })}` : ""}
      </p>
    </div>
  );
}
